import React from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';
import { useUpgradeAction } from '../hooks/useUpgradeAction';
import {
  getPermissionConfig,
  getViewPermissionConfig,
  type ViewType,
} from '../config/permissions';

interface ViewGateProps {
  viewType: ViewType;
  children: ReactNode;
}

/**
 * Route-level tier gate for a full-page view.
 *
 * The widget equivalent is `PermissionGate`; this is deliberately a separate
 * component rather than a prop on that one, because a view is not a dashboard
 * tile: there is no `onRemove` affordance, it fills the page instead of a grid
 * cell, and it offers a way back to the dashboard so a gated user is not
 * stranded on a dead route.
 *
 * As with `PermissionGate`, `children` are NEVER instantiated when locked, so a
 * gated view mounts nothing and issues no requests.
 *
 * ⚠️ UX gate, not a security boundary — see `VIEW_PERMISSIONS`.
 */
export const ViewGate: React.FC<ViewGateProps> = ({ viewType, children }) => {
  const { hasAnyPermission } = usePermissions();

  const viewConfig = getViewPermissionConfig(viewType);
  // An unconfigured view is left open rather than bricked: a missing entry is a
  // config mistake, and failing closed would hide a whole page with no clue why.
  const hasAccess = viewConfig ? hasAnyPermission(viewConfig.requiredPermission) : true;

  const { onUpgrade, isRedirecting } = useUpgradeAction(!hasAccess);

  if (hasAccess) {
    return <>{children}</>;
  }

  const permission = getPermissionConfig(viewConfig!.requiredPermission[0]);

  return (
    <div className="flex items-center justify-center min-h-[60vh] px-6">
      <div className="text-center max-w-md">
        <div className="mb-4">
          <svg
            className="w-16 h-16 mx-auto text-accent-yellow"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M2.5 19h19v2h-19v-2zm19.57-9.36c-.21-.8-1.04-1.28-1.84-1.06l-4.23 1.12-3.47-5.46a1.5 1.5 0 00-2.56 0L6.5 9.7l-4.23-1.12c-.8-.22-1.63.26-1.84 1.06-.21.8.26 1.63 1.06 1.84l4.53 1.2v6.32h12v-6.32l4.53-1.2c.8-.21 1.27-1.04 1.06-1.84z" />
          </svg>
        </div>

        <h3 className="text-xl font-semibold text-gray-100 mb-2">
          {viewConfig!.displayName} Locked
        </h3>

        <p className="text-sm text-gray-300 mb-1">
          This page requires{' '}
          <span className="font-medium text-accent-yellow">
            {permission?.displayName ?? 'an upgrade'}
          </span>
        </p>

        {permission?.description && (
          <p className="text-xs text-gray-400 mb-6">{permission.description}</p>
        )}

        <div className="flex items-center justify-center gap-3">
          <button
            onClick={onUpgrade}
            disabled={isRedirecting}
            className="inline-flex items-center gap-2 px-6 py-3 bg-bull-blue hover:bg-bull-blue/90 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
            {isRedirecting ? 'Redirecting...' : 'Upgrade Now'}
          </button>

          <Link
            to="/"
            className="px-6 py-3 text-gray-300 hover:text-white font-medium rounded-lg transition-colors"
          >
            Back to dashboard
          </Link>
        </div>
      </div>
    </div>
  );
};
